/**
 * HEIC / HEIF → JPEG conversion helper.
 *
 * iPhones capture photos as HEIC/HEIF, which no browser can decode for
 * `<img>` preview. This converts them to JPEG in the browser BEFORE upload so
 * the file both previews and stays broadly compatible.
 *
 * The `heic2any` decoder is heavy (bundled libheif WASM), so it is imported
 * lazily and ONLY when a HEIC/HEIF file is actually detected — the dynamic
 * `import()` means Vite code-splits it into its own chunk that never loads for
 * the common JPEG/PNG path.
 *
 * Non-lossy failure contract: on ANY conversion failure we return the ORIGINAL
 * file with `failed: true` (never throw). Callers upload the original anyway so
 * no photo is ever lost — they just surface a gentle "won't preview" warning.
 */

const HEIC_MIME = /^image\/hei[cf]$/i;
const HEIC_EXT = /\.hei[cf]$/i;

/** True when the file looks like HEIC/HEIF by mime OR extension. Browsers
 *  frequently report an empty or `application/octet-stream` mime for HEIC, so
 *  the extension check is the reliable signal. */
export function isHeic(file: File): boolean {
  return HEIC_MIME.test(file.type) || HEIC_EXT.test(file.name);
}

export interface HeicConvertResult {
  /** The file to upload: the converted JPEG on success, else the original. */
  file: File;
  /** True when a real HEIC→JPEG conversion happened. */
  converted: boolean;
  /** True when detection said HEIC but conversion threw — original returned. */
  failed: boolean;
}

/**
 * Convert a HEIC/HEIF file to JPEG. No-op passthrough for every other type.
 * Never throws — see the non-lossy failure contract above.
 */
export async function maybeConvertHeic(file: File): Promise<HeicConvertResult> {
  if (!isHeic(file)) {
    return { file, converted: false, failed: false };
  }
  try {
    const { default: heic2any } = await import('heic2any');
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(out) ? out[0] : out;
    if (!blob) {
      return { file, converted: false, failed: true };
    }
    const base = file.name.replace(HEIC_EXT, '') || 'photo';
    const converted = new File([blob], `${base}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
    return { file: converted, converted: true, failed: false };
  } catch {
    // Decode failed (corrupt / unsupported HEIC variant). Keep the original so
    // the upload is still non-lossy; the caller warns it won't preview.
    return { file, converted: false, failed: true };
  }
}
